import {
    COLLABORATION_METHOD_ASYNC,
    COLLABORATION_METHOD_OPTION_KEY,
    COLLABORATION_METHOD_SYNC,
} from '@99designs/design-services-common';
import { useProductOptionsLazyQuery } from '../../../../../graph/ProductOptions.generated';
import { useServiceOptionsUpdatedMutation } from '../../GridChoiceField/serviceOptionsUpdated.generated';
import { useSelectProductMutation } from './selectProduct.generated';

export function mergeServiceProductOptions(
    newProductOptions: ReadonlyArray<{
        key: { key: string };
        values: ReadonlyArray<{ key: string; isPreselected: boolean }>;
    }>,
    existingOptions: ReadonlyArray<{ key: string; value: string }> | null | undefined,
    withDesignLive?: boolean
): Array<{ key: string; value: string }> {
    const existingMap = new Map((existingOptions ?? []).map(({ key, value }) => [key, value]));

    return newProductOptions.reduce<Array<{ key: string; value: string }>>((acc, option) => {
        const optionKey = option.key.key;
        const existingValue = existingMap.get(optionKey);

        if (optionKey === COLLABORATION_METHOD_OPTION_KEY) {
            acc.push({
                key: optionKey,
                value: withDesignLive ? COLLABORATION_METHOD_SYNC : COLLABORATION_METHOD_ASYNC,
            });
        } else if (existingValue !== undefined) {
            acc.push({ key: optionKey, value: existingValue });
        } else {
            const preselected = option.values.find((v) => v.isPreselected);
            if (preselected) {
                acc.push({ key: optionKey, value: preselected.key });
            }
        }

        return acc;
    }, []);
}

export function useSelectProductWithCacheUpdate() {
    const [fetchProductOptions] = useProductOptionsLazyQuery();
    const [serviceOptionsUpdated] = useServiceOptionsUpdatedMutation();

    const [selectProduct] = useSelectProductMutation({
        update: (cache, { data }) => {
            if (data && data.addEvent_SelectProduct) {
                cache.modify({
                    id: cache.identify(data.addEvent_SelectProduct),
                    fields: {
                        expertService: () => data.addEvent_SelectProduct.expertService,
                        facts: () => data.addEvent_SelectProduct.facts,
                    },
                });
            }
        },
        onCompleted: async (data) => {
            const request = data.addEvent_SelectProduct;
            const productKey = request.expertService?.serviceProduct?.productKey;
            const version = request.expertService?.serviceProduct?.pimMerchantVersion;

            if (!productKey || version == null) {
                return;
            }

            const { data: productOptionsData } = await fetchProductOptions({
                variables: { key: productKey, version },
            });

            const newProductOptions = productOptionsData?.productOptions;
            if (!newProductOptions?.length) {
                return;
            }

            const existingOptions = request.facts?.serviceProductOptions;
            const withDesignLive = request.facts?.withDesignLive;

            const merged = mergeServiceProductOptions(
                newProductOptions,
                existingOptions,
                withDesignLive
            );

            if (merged.length > 0) {
                await serviceOptionsUpdated({
                    variables: {
                        requestId: request.id,
                        input: {
                            options: merged,
                        },
                    },
                });
            }
        },
    });

    return { selectProduct };
}
